import React from "react";
import { createPortal } from "react-dom";
import { X, ArrowUpRight, CalendarClock } from "lucide-react";

const SUPPORT_WHATSAPP_NUMBER = "919036219589";
const WHATSAPP_MESSAGE = "Hi! I'm interested in the paid Salesforce Data Cloud mentorship course, or I need help with a project. Please share more details.";
const WHATSAPP_LINK = `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;
const WHATSAPP_QR_CODE_URL = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&bgcolor=ffffff&data=${encodeURIComponent(WHATSAPP_LINK)}`;
const SCHEDULE_CALL_LINK = "https://meetpro.club/ELearningit";
const ADVISOR_PHOTO_URL = "https://images.unsplash.com/photo-1500648767791-00dcc994a43e";

function WhatsAppIcon({ className }) {
  return (
    <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
      <path d="M16 0C7.163 0 0 7.163 0 16c0 2.837.744 5.5 2.04 7.803L.05 31.5a.5.5 0 0 0 .61.61l7.7-1.99A15.93 15.93 0 0 0 16 32c8.837 0 16-7.163 16-16S24.837 0 16 0Zm0 29.2a13.15 13.15 0 0 1-6.71-1.84l-.48-.29-5.02 1.3 1.33-4.9-.31-.5A13.18 13.18 0 1 1 29.2 16 13.2 13.2 0 0 1 16 29.2Zm7.24-9.88c-.4-.2-2.35-1.16-2.72-1.29-.36-.13-.63-.2-.9.2-.27.4-1.03 1.29-1.26 1.55-.23.27-.46.3-.86.1-.4-.2-1.68-.62-3.2-1.98-1.18-1.06-1.98-2.36-2.21-2.76-.23-.4-.02-.61.19-.81.2-.2.4-.5.6-.76.2-.27.27-.46.4-.76.13-.3.07-.56-.07-.76-.13-.2-.62-1.5-.85-2.03-.23-.5-.46-.44-.63-.44-.16 0-.36-.02-.56-.02-.2 0-.5.07-.76.36-.27.3-1.02 1-1.02 2.43 0 1.43 1.04 2.82 1.19 3.02.15.2 2.02 3.1 4.9 4.22 2.87 1.13 2.87.75 3.39.7.53-.05 1.7-.7 1.94-1.36.23-.66.23-1.23.16-1.35-.07-.13-.26-.2-.66-.4Z" />
    </svg>
  );
}

export default function HelpSupportModal({ open, onClose }) {
  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      data-testid="help-support-modal"
      onClick={onClose}
    >
      <div
        className="glass-panel rounded-2xl p-6 sm:p-8 max-w-2xl w-full border border-indigo-400/20 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="help-support-modal-close-button"
          className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex flex-col items-center text-center mb-6">
          <img
            src={ADVISOR_PHOTO_URL}
            alt="Your advisor"
            loading="lazy"
            data-testid="help-support-advisor-photo"
            className="w-16 h-16 rounded-full object-cover border-2 border-indigo-400/40 mb-4"
          />
          <h2 className="font-heading text-xl sm:text-2xl font-semibold text-white">Connect with your advisor</h2>
          <p className="text-sm text-slate-400 mt-2 max-w-md">
            Looking for our paid Salesforce Data Cloud mastery course, or need help with a real project? Reach out to our team below.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-xl border border-white/10 bg-white/5 p-5 flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-[#25D366]/15 flex items-center justify-center mb-3">
              <WhatsAppIcon className="w-6 h-6 text-[#25D366]" />
            </div>
            <p className="text-base font-semibold text-white">Chat on WhatsApp</p>
            <p className="text-xs text-slate-400 mt-1 mb-4">Get quick answers on the paid course, project support, or account benefits.</p>

            <img
              src={WHATSAPP_QR_CODE_URL}
              alt="Scan to chat on WhatsApp"
              loading="lazy"
              data-testid="help-support-whatsapp-qr"
              className="w-36 h-36 rounded-lg mb-4 bg-white p-1.5"
            />

            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              data-testid="help-support-whatsapp-chat-button"
              className="w-full flex items-center justify-center gap-1.5 bg-[#25D366] hover:bg-[#20BD5A] text-white text-sm font-semibold rounded-lg px-4 py-2.5 transition-colors"
            >
              Chat Now <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
            <p className="text-[11px] text-slate-500 mt-3">Typically replies within minutes</p>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-5 flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center mb-3">
              <CalendarClock className="w-6 h-6 text-indigo-300" />
            </div>
            <p className="text-base font-semibold text-white">Schedule a Call</p>
            <p className="text-xs text-slate-400 mt-1 mb-4">Book a free session to discuss the paid course, project support, or your career plan.</p>

            <div className="flex-1" />

            <a
              href={SCHEDULE_CALL_LINK}
              target="_blank"
              rel="noreferrer"
              data-testid="help-support-schedule-call-button"
              className="w-full flex items-center justify-center gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold rounded-lg px-4 py-2.5 transition-colors"
            >
              Schedule Now <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
            <p className="text-[11px] text-slate-500 mt-3">Free 30-min session</p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
